import classNames from "classnames";
import { AddOwnersModal } from "PFApp/activities/show/parts/add_owners_modal";
import AppContext from "PFApp/app_context";
import { useMetadata } from "PFApp/components/activity_commons/activity_details_more_info";
import { TEMPLATE_KEYS } from "PFApp/constants/templates";
import useIsAuditPlannerPermitted from "PFApp/use_is_audit_planner_permitted";
import { Button } from "PFComponents/button";
import ToggleableText from "PFComponents/toggleable_text/toggleable_text";
import Tooltip from "PFComponents/tooltip/tooltip";
import { getProfileName } from "PFCore/helpers/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import CalendarIcon from "PFIcons/calendar_fixed.svg";
import CheckIcon from "PFIcons/check.svg";
import ClipboardListIcon from "PFIcons/clipboard_list.svg";
import HourglassIcon from "PFIcons/hourglass.svg";
import InfoIcon from "PFIcons/info.svg";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useRowIconComponent } from "../../../hooks/use_row_icon_component";
import css from "../../../parts/overview/detail_view/detail_view.module.scss";
import BookingActivityContextProvider, {
  useBookingActivityContext
} from "../../../parts/providers/booking_activity_context_provider";
import { DetailsHeader } from "./details_header";

const EngagementDetailContent = ({ onClose }) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { formatDate, utc } = useDateFormatter();
  const { t } = useTranslation("bookingModule");

  const [showAddOwners, setShowAddOwners] = useState(false);
  const { activity, id } = useBookingActivityContext();
  const isAuditPlannerPermitted = useIsAuditPlannerPermitted();
  const ActivityIconComponent = useRowIconComponent({
    templateId: activity?.template_id,
    type: TEMPLATE_KEYS.ENGAGEMENT
  });

  const metadata = useMetadata({ activity });
  const filteredMetadata = metadata.filter(
    ({ type, title }) => "availability" !== type && !["Skills", "Duration"].includes(title)
  );

  if (!activity) {
    return null;
  }

  const {
    profile,
    post_until,
    duration,
    name,
    owners,
    description,
    created_at,
    coowned,
    children_workflow_states_tally,
    template_id
  } = activity;

  const daysLeft = -Math.trunc(utc().diff(utc(post_until), "days", true));
  const rolesTotal = Object.values(children_workflow_states_tally).reduce((sum, num) => sum + num, 0);
  const rolesBooked = children_workflow_states_tally.booked || 0;
  const isAuditEngagement =
    currentProfile.templates.find(({ id }) => id === template_id)?.key === TEMPLATE_KEYS.AUDIT_ENGAGEMENT;
  const shouldDisplayActivityLink =
    !isAuditEngagement ||
    (isAuditPlannerPermitted && activity.owners.some(({ profile_id }) => profile_id === currentProfile.id));
  const linkUrl = isAuditEngagement ? `/audit_planner/control_centre/${id}` : `/activities/${id}`;

  return (
    <div className={classNames(css.root)}>
      <DetailsHeader
        title={name}
        subtitle={isAuditEngagement ? t("auditEngagement") : t("engagement")}
        onClose={onClose}
        shouldDisplayActivityLink={shouldDisplayActivityLink}
        linkUrl={linkUrl}
      >
        <ActivityIconComponent
          iconProps={{ viewBox: "4 0 20 20", height: 35, fill: "var(--Palette-text)" }}
          wrapperProps={{
            classes: {
              container: css.auditActivityIconContainer,
              badge: css.auditActivityIconBadge
            },
            badgeIconProps: { width: 12 }
          }}
        />
      </DetailsHeader>
      <div className={css.item}>
        <div className={css.label}> {t("owner")}</div>
        <span className={css.spaceBetween}>
          <div>
            {owners.map(({ profile }, index) => (
              <React.Fragment key={profile.id}>
                <Link to={`/profiles/${profile.id}`}>{getProfileName(profile)}</Link>
                {index !== owners.length - 1 && ", "}
              </React.Fragment>
            ))}
          </div>

          {coowned && <Button icon="add" kind="tertiary" onClick={() => setShowAddOwners(true)} />}
        </span>
      </div>
      <div className={css.item}>
        <div className={css.label}> {t("description")}</div>
        <ToggleableText text={description} />
      </div>
      <span className={classNames(css.item, css.duration, css.spaceBetween)}>
        <span className={css.alignCenter}>
          <CalendarIcon viewBox="3 0 20 20" height={20} fill="var(--Palette-base)" />
          <span className={css.label}>{t("duration")} </span>
          {duration ? (
            <>
              <span className={css.strong}>
                {duration.start_date ? formatDate(utc(duration.start_date)) : "-"}
              </span>
              <span className={css.text}> {t("details.durationTo")}</span>
              <span className={css.strong}>
                {duration.end_date ? formatDate(utc(duration.end_date)) : "-"}
              </span>
            </>
          ) : (
            "-"
          )}
        </span>
      </span>
      <span className={css.details}>
        <span className={css.item}>
          <div className={css.label}>{t("postUntil")}</div>
          <span className={css.alignCenter}>
            <HourglassIcon viewBox="7 1 12 17" className={css.icon} />
            {t("day", { count: daysLeft })}
          </span>
        </span>
        <span className={css.item}>
          <div className={css.label}>{t("createdAt")}</div>
          <span className={css.alignCenter}>
            <CalendarIcon viewBox="3 0 20 20" className={css.icon} />
            {formatDate(utc(created_at))}
          </span>
        </span>
        <span className={css.item}>
          <div className={css.label}>{t("roles")}</div>
          <span className={css.alignCenter}>
            <ClipboardListIcon viewBox="2 -1 21 23" className={css.icon} />
            <Link to={`/activities/${id}/roles`}>{rolesTotal}</Link>
          </span>
        </span>
        <span className={css.item}>
          <div className={css.label}>{t("booked")}</div>
          <span className={css.alignCenter}>
            <CheckIcon viewBox="80 -170 512 800" className={css.icon} />
            <Link to={`/activities/${id}/roles`}>{rolesBooked}</Link>
          </span>
        </span>
      </span>
      <div className={css.separator} />
      <div className={css.item}>
        <div className={css.label}>{t("privacy")}</div>
        <span className={css.alignCenter}>
          {t(activity.private ? "private" : "public")}
          {activity.private && (
            <Tooltip content={t("details.privacyTooltipText")} appendTo="parent">
              <span className={css.alignCenter}>
                <InfoIcon height={20} width={20} className={css.icon} />
              </span>
            </Tooltip>
          )}
        </span>
      </div>
      <span className={css.details}>
        {filteredMetadata.map(
          ({ title, text }) =>
            text !== undefined &&
            text !== "" && (
              <div key={title} className={css.item}>
                <div className={css.label}>{title}</div>
                <span>{text}</span>
              </div>
            )
        )}
      </span>

      <div className={css.separator} />
      <div className={css.item}>
        <div className={css.label}>{t("creator")}</div>
        <Link to={`/profiles/${profile.id}`}>{getProfileName(profile)}</Link>
      </div>
      {showAddOwners && <AddOwnersModal handleClose={() => setShowAddOwners(false)} activity={activity} />}
    </div>
  );
};

const EngagementDetail = ({ data, onClose }) => (
  <BookingActivityContextProvider id={data.id}>
    <EngagementDetailContent onClose={onClose} />
  </BookingActivityContextProvider>
);

EngagementDetail.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired
  }),
  onClose: PropTypes.func.isRequired
};

EngagementDetailContent.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default EngagementDetail;
